import cn from 'classnames';
import React from 'react';
import { Link } from 'react-router-dom';
import DownloadAppStore from 'src/assets/svg/footer/download-app-store.svg';
import DownloadGooglePlay from 'src/assets/svg/footer/download-google-play.svg';
import PlaidClothMobile from 'src/assets/svg/footer/plaid-cloth-mobile.svg';
import PlaidCloth from 'src/assets/svg/footer/plaid-cloth.svg';
import FacebookSocialIcon from 'src/assets/svg/footer/social-facebook.svg';
import InstagramSocialIcon from 'src/assets/svg/footer/social-instagram.svg';
import TikTokSocialIcon from 'src/assets/svg/footer/social-tiktok.svg';
import LogoRaspberry from 'src/assets/svg/logo/logo-raspberry.svg';
import { APP_STORE_HREF, GOOGLE_PLAY_HREF } from 'src/shared/Constants';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';

const horizontalDivider = <div className='my-8 border-t border-eggplant-100 md:my-0' />;

/* Hints to web crawlers that we DO NOT want them to follow these links or share our precious page authority with them
 * Web crawlers do not have to obey rel='nofollow', but they promise to consider it
 *
 * Please use this component for links leading to the main Shef site to ensure we always include rel='nofollow'
 */
const NoFollowLink: React.FC<{ to: string; className?: string; children: React.ReactNode }> = ({
  children,
  to,
  ...restProps
}) => (
  <a rel='nofollow' href={to} {...restProps}>
    {children}
  </a>
);

type StyledLinkProps = React.RefAttributes<HTMLAnchorElement> & {
  to: string;
  nofollow?: boolean;
  className?: string;
  children: React.ReactNode;
};

const StyledLink: React.FC<StyledLinkProps> = ({ to, nofollow, className, children, ...linkProps }) => {
  const linkCSS = cn(className, 'text-eggplant-600 hover:text-radish-200');

  if (nofollow) {
    return (
      <NoFollowLink to={to} className={linkCSS} {...linkProps}>
        {children}
      </NoFollowLink>
    );
  }
  return (
    <Link to={to} className={linkCSS} {...linkProps}>
      {children}
    </Link>
  );
};

/**
 * -------------  FOOTER SECTIONS -------------
 */

const FooterList: React.FC<React.PropsWithChildren<{ title?: string; className?: string }>> = ({
  title,
  className,
  children,
}) => (
  <div className={`${className ?? ''} flex w-28 flex-col text-left`}>
    <span className='mb-8 text-xs font-semibold text-neutral-800'>{title}</span>
    <div className='flex flex-col gap-1 text-eggplant-400'>{children}</div>
  </div>
);

const LegalLinks: React.FC = () => (
  <div className='flex justify-center gap-4 md:flex-col md:gap-1'>
    <StyledLink to='/terms-of-service'>Terms of Service</StyledLink>
    <StyledLink to='/privacy-policy'>Privacy Policy</StyledLink>
  </div>
);

const Socials: React.FC = () => (
  <section className='flex justify-center gap-10 py-8 md:justify-start md:gap-2 md:p-0'>
    <StyledLink to='https://www.facebook.com/ordershef' nofollow>
      <img src={FacebookSocialIcon} alt='Facebook' className='size-6' />
    </StyledLink>

    <StyledLink to='https://www.instagram.com/ordershef' nofollow>
      <img src={InstagramSocialIcon} alt='Instagram' className='size-6' />
    </StyledLink>

    <StyledLink to='https://www.tiktok.com/@ordershef' nofollow>
      <img src={TikTokSocialIcon} alt='TikTok' className='size-6' />
    </StyledLink>
  </section>
);

const LinksColumns: React.FC = () => {
  const { isSmallScreen } = useScreenContext();
  const blogUrl = process.env.REACT_APP_PUBLIC_BLOG_URL ?? '';
  return (
    <section className='flex justify-center gap-20 md:w-full md:grid-cols-3 md:gap-3 md:px-0'>
      <div className='md:w-[30%]'>
        <FooterList title='LEARN'>
          <StyledLink to={`${blogUrl}/our-story`} nofollow>
            Our Story
          </StyledLink>
          <StyledLink nofollow to='https://shef.com/homemade-food/food-safety'>
            Food Safety
          </StyledLink>
          <StyledLink to='https://help.shef.com/en/' nofollow>
            Customer Support
          </StyledLink>
        </FooterList>
      </div>
      <div className='md:w-[30%]'>
        <FooterList title='RESOURCES'>
          <StyledLink to='/become-a-shef'>Become a shef</StyledLink>
          <StyledLink to='/careers'>Careers</StyledLink>
          <StyledLink to='/gift'>Gift cards</StyledLink>
          <StyledLink to='/homemade-food-delivery'>Homemade food delivery</StyledLink>
          {!isSmallScreen && <LegalLinks />}
        </FooterList>
      </div>
      {!isSmallScreen && (
        <div className='md:w-[30%]'>
          <Socials />
        </div>
      )}
    </section>
  );
};

const AppDownload: React.FC = () => (
  <div className='mx-auto flex-col items-center'>
    <h3 className='mx-auto w-80 text-1xl font-bold text-eggplant-600 md:mx-0'>
      DOWNLOAD OUR APP AND LEAVE THE REST TO THE SHEFS
    </h3>
    <div className='flex justify-center gap-6 pt-4 md:justify-start'>
      <StyledLink to={APP_STORE_HREF} nofollow>
        <img src={DownloadAppStore} alt='Download on the App Store' />
      </StyledLink>

      <StyledLink to={GOOGLE_PLAY_HREF} nofollow>
        <img src={DownloadGooglePlay} alt='Get it on Google Play' />
      </StyledLink>
    </div>
  </div>
);

const ShefCopyright = () => <div className='mt-4'>Shef, Inc. {new Date().getFullYear()}. All Rights Reserved.</div>;

const FooterSectionDesktop: React.FC = () => (
  <div className='bg-eggplant-50'>
    {horizontalDivider}
    <div className='flex h-full grid-cols-2 gap-4 bg-eggplant-50 px-10 pt-24'>
      {/* Left section */}
      <section className='w-full'>
        <img src={LogoRaspberry} alt='Shef' className='mb-16 mt-1 inline-block' />
        <AppDownload />
        <ShefCopyright />
      </section>

      {/* Right section */}
      <div className='flex w-full align-top'>
        <LinksColumns />
      </div>
    </div>

    {/* Plaid Cloth Image */}
    <img src={PlaidCloth} alt='' className='w-full pt-24' />
  </div>
);

const FooterSectionMobile: React.FC = () => (
  <div className='h-full flex-col items-center justify-center bg-eggplant-50 pt-16 text-center'>
    <AppDownload />

    <div className='px-4'>
      {horizontalDivider}
      <LinksColumns />
      {horizontalDivider}

      <img src={LogoRaspberry} alt='Shef' className='mt-1 inline-block' />

      <Socials />
      <LegalLinks />
      <ShefCopyright />
    </div>

    <img src={PlaidClothMobile} alt='' className='w-full pt-24' />
  </div>
);

export const Footer: React.FC = () => {
  const { isSmallScreen } = useScreenContext();

  const renderFooter = isSmallScreen ? <FooterSectionMobile /> : <FooterSectionDesktop />;
  return renderFooter;
};
